import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

/**
 * Shared QueryClient singleton with VTAK defaults for all REST queries. Live
 * feeds stay on the WebSocket/zustand path — Query is only for request/response
 * REST data. Exported as a module singleton so non-React modules (e.g. the
 * geocode search provider) can read/write the same cache via `fetchQuery`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // most VTAK REST data is fine for ~1 min
      gcTime: 5 * 60_000, // keep unmounted-panel caches 5 min
      retry: 1, // tactical: fail fast, don't hammer
      refetchOnWindowFocus: false, // ops dashboards aren't tab-switch sensitive
      refetchOnReconnect: true,
    },
    mutations: { retry: 0 },
  },
});

/**
 * Slow-changing query families worth surviving a reload — reference data and
 * workspace config render instantly from storage while a fresh fetch runs.
 * Live/track/mission data is intentionally excluded.
 */
const PERSISTED_KEY_PREFIXES: ReadonlySet<string> = new Set(['workspace', 'config']);

/**
 * Cache-buster for the persisted store: a new deploy (new build id) discards
 * the persisted cache, so returning users can't ride a pre-deploy config
 * manifest for up to maxAge. Dev uses a constant so HMR/dev-server restarts
 * don't thrash the cache (and non-Vite runtimes never read __BUILD_ID__).
 */
const cacheBuster = import.meta.env.DEV ? 'dev' : __BUILD_ID__;

persistQueryClient({
  queryClient,
  persister: createSyncStoragePersister({ storage: window.localStorage, key: 'vtak.query-cache' }),
  maxAge: 24 * 60 * 60_000,
  buster: cacheBuster,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      query.state.status === 'success' &&
      typeof query.queryKey[0] === 'string' &&
      PERSISTED_KEY_PREFIXES.has(query.queryKey[0]),
  },
});
